<template>
  <div class="demands-page">
    <el-row :gutter="16" class="stats-row">
      <el-col v-for="card in statCards" :key="card.key" :xs="24" :sm="12" :lg="6">
        <el-card shadow="hover" class="stat-card" :body-style="{ padding: '20px' }">
          <div class="stat-top">
            <div>
              <div class="stat-label">{{ card.label }}</div>
              <div class="stat-value">{{ card.value }}</div>
              <div class="stat-extra" :class="card.extraClass">{{ card.extra }}</div>
            </div>
            <div class="stat-icon" :style="{ background: card.bg }">
              <i :class="card.icon" />
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="table-card">
      <div class="toolbar">
        <el-input
          v-model="keyword"
          placeholder="搜索需求标题、发布人..."
          clearable
          class="toolbar-search"
          @keyup.enter.native="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch" />
        </el-input>
        <el-select v-model="filterStatus" placeholder="全部状态" clearable class="toolbar-filter" @change="handleSearch">
          <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
        <el-select v-model="skillTag" placeholder="全部技能" clearable class="toolbar-filter" @change="handleSearch">
          <el-option v-for="cat in skillTypes" :key="cat.id" :label="cat.name" :value="cat.name" />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-plus" style="margin-left: auto" @click="openForm(null)">新增需求</el-button>
      </div>

      <el-table v-loading="loading" :data="list" stripe class="demand-table">
        <el-table-column prop="title" label="需求标题" min-width="180" show-overflow-tooltip />
        <el-table-column label="发布人" width="130">
          <template slot-scope="{ row }">{{ row.publisherName || `用户${row.userId}` }}</template>
        </el-table-column>
        <el-table-column label="预算" width="110">
          <template slot-scope="{ row }">{{ formatBudget(row.budget) }}</template>
        </el-table-column>
        <el-table-column label="技能标签" min-width="160">
          <template slot-scope="{ row }">
            <el-tag v-for="tag in parseSkillTags(row.skillTags).slice(0, 3)" :key="tag" size="mini" class="skill-tag">
              {{ tag }}
            </el-tag>
            <span v-if="!parseSkillTags(row.skillTags).length" class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="发布时间" width="120">
          <template slot-scope="{ row }">{{ formatUserDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="截止时间" width="120">
          <template slot-scope="{ row }">{{ formatUserDate(row.deadline) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="demandStatusTagType(row.status)">
              {{ labelOf(DEMAND_STATUS, row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openDetail(row)">详情</el-button>
            <el-button size="mini" type="primary" plain icon="el-icon-edit" @click="openForm(row)">编辑</el-button>
            <template v-if="Number(row.status) === 0">
              <el-button size="mini" type="success" plain @click="approveDemand(row)">通过</el-button>
              <el-button size="mini" type="danger" plain @click="rejectDemand(row)">驳回</el-button>
            </template>
            <el-button
              v-else
              size="mini"
              type="primary"
              plain
              @click="openStatusDialog(row)"
            >改状态</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadList"
      />
    </el-card>

    <el-dialog
      :visible.sync="detailVisible"
      width="520px"
      top="8vh"
      custom-class="demand-detail-dialog"
      @closed="handleDetailClosed"
    >
      <div v-if="detail" slot="title" class="detail-title">
        <span class="detail-icon"><i class="el-icon-document" /></span>
        <span>需求详情</span>
      </div>

      <div v-if="detail" v-loading="detailLoading" class="detail-body">
        <div class="demand-title">{{ detail.title }}</div>

        <div class="info-list">
          <div class="info-row">
            <span class="info-label">需求编号</span>
            <span class="info-value">{{ formatDemandCode(detail.id) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">发布人</span>
            <span class="info-value">{{ publisherDisplay(detail) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">预算</span>
            <span class="info-value budget-value">{{ formatBudget(detail.budget) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">发布时间</span>
            <span class="info-value">{{ formatUserDate(detail.createdAt) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">截止时间</span>
            <span class="info-value">{{ formatUserDate(detail.deadline) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">状态</span>
            <span class="info-value">
              <el-tag size="mini" :type="demandStatusTagType(detail.status)">
                {{ labelOf(DEMAND_STATUS, detail.status) }}
              </el-tag>
            </span>
          </div>
          <div class="info-row info-row-tags">
            <span class="info-label">技能标签</span>
            <div class="info-value">
              <span
                v-for="tag in parseSkillTags(detail.skillTags)"
                :key="tag"
                class="skill-pill"
              >{{ tag }}</span>
              <span v-if="!parseSkillTags(detail.skillTags).length" class="text-muted">-</span>
            </div>
          </div>
        </div>

        <div class="desc-section">
          <div class="desc-title">需求描述</div>
          <div class="desc-content">{{ detail.description || '暂无描述' }}</div>
        </div>
      </div>

      <div v-if="detail" slot="footer" class="detail-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="更新需求状态" :visible.sync="statusVisible" width="400px">
      <el-form label-width="80px">
        <el-form-item label="状态">
          <el-select v-model="statusForm.status" style="width: 100%">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="statusVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitStatus">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="form.id ? '编辑需求' : '新增需求'"
      :visible.sync="formVisible"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form ref="demandForm" :model="form" :rules="formRules" label-width="96px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="发布用户ID">
          <el-input-number v-model="form.userId" :min="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="预算(元)">
          <el-input-number v-model="form.budget" :min="0" :precision="2" style="width: 100%" />
        </el-form-item>
        <el-form-item label="截止日期">
          <el-date-picker v-model="form.deadline" type="date" value-format="yyyy-MM-dd" style="width: 100%" />
        </el-form-item>
        <el-form-item label="技能标签">
          <el-input v-model="form.skillTags" placeholder="多个标签用逗号分隔" />
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input v-model="form.contactPhone" />
        </el-form-item>
        <el-form-item label="微信号">
          <el-input v-model="form.contactWechat" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width: 100%">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDemands, getDemandStats, getDemandDetail, updateDemandStatus, getDemandCategories, saveDemand } from '@/api/admin'
import { DEMAND_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import { formatCount } from '@/utils/dashboardFormat'
import { formatBudget, formatDemandAmount, formatDemandCode, demandStatusTagType } from '@/utils/demandFormat'
import { formatUserDate, formatUserCode, parseSkillTags } from '@/utils/userFormat'

function emptyDemandForm() {
  return {
    id: null,
    title: '',
    description: '',
    userId: 1,
    budget: 0,
    deadline: '',
    skillTags: '',
    contactPhone: '',
    contactWechat: '',
    status: 1
  }
}

export default {
  name: 'DemandsList',
  data() {
    return {
      DEMAND_STATUS,
      loading: false,
      submitting: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      keyword: '',
      filterStatus: null,
      skillTag: null,
      stats: {},
      detailVisible: false,
      detailLoading: false,
      statusVisible: false,
      detail: null,
      statusForm: { id: null, status: null },
      skillTypes: [],
      formVisible: false,
      form: emptyDemandForm(),
      formRules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        description: [{ required: true, message: '请输入描述', trigger: 'blur' }]
      }
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(DEMAND_STATUS)
    },
    statCards() {
      return [
        {
          key: 'total',
          label: '总发布需求',
          value: formatCount(this.stats.totalCount),
          extra: `↑ 本月新增 +${formatCount(this.stats.monthNewCount)}`,
          extraClass: 'up',
          icon: 'el-icon-document',
          bg: 'rgba(64, 158, 255, 0.12)'
        },
        {
          key: 'pending',
          label: '待审核',
          value: formatCount(this.stats.pendingCount),
          extra: '需尽快处理',
          extraClass: 'warn',
          icon: 'el-icon-warning-outline',
          bg: 'rgba(245, 158, 11, 0.12)'
        },
        {
          key: 'matching',
          label: '撮合中',
          value: formatCount(this.stats.matchingCount),
          extra: '等待对接',
          extraClass: '',
          icon: 'el-icon-connection',
          bg: 'rgba(139, 92, 246, 0.12)'
        },
        {
          key: 'amount',
          label: '需求成交额',
          value: formatDemandAmount(this.stats.successAmount),
          extra: '累计成交金额',
          extraClass: '',
          icon: 'el-icon-money',
          bg: 'rgba(16, 185, 129, 0.12)'
        }
      ]
    }
  },
  created() {
    this.loadSkillTypes()
    this.loadStats()
    this.loadList()
  },
  methods: {
    labelOf,
    formatBudget,
    formatDemandCode,
    formatUserDate,
    parseSkillTags,
    demandStatusTagType,
    publisherDisplay(detail) {
      const name = detail.publisherName || `用户${detail.userId}`
      return `${name} ${formatUserCode(detail.userId)}`
    },
    handleDetailClosed() {
      this.detail = null
    },
    async loadSkillTypes() {
      try {
        this.skillTypes = await getDemandCategories() || []
      } catch (e) {
        this.skillTypes = []
      }
    },
    handleSearch() {
      this.page = 1
      this.loadList()
    },
    openForm(row) {
      this.form = row
        ? {
            id: row.id,
            title: row.title,
            description: row.description,
            userId: row.userId || 1,
            budget: row.budget || 0,
            deadline: row.deadline,
            skillTags: row.skillTags || '',
            contactPhone: row.contactPhone || '',
            contactWechat: row.contactWechat || '',
            status: row.status == null ? 1 : row.status
          }
        : emptyDemandForm()
      this.formVisible = true
    },
    submitForm() {
      this.$refs.demandForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await saveDemand(this.form)
          this.$message.success('保存成功')
          this.formVisible = false
          this.loadList()
          this.loadStats()
        } catch (e) {
          this.$message.error((e && e.message) || '保存失败')
        } finally {
          this.submitting = false
        }
      })
    },
    async loadStats() {
      try {
        this.stats = await getDemandStats() || {}
      } catch (e) {
      }
    },
    async loadList() {
      this.loading = true
      try {
        const result = await getDemands({
          page: this.page,
          size: this.pageSize,
          keyword: this.keyword || undefined,
          status: this.filterStatus != null ? this.filterStatus : undefined,
          skillTag: this.skillTag || undefined
        })
        this.list = result.list || []
        this.total = result.total || 0
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      try {
        this.detail = await getDemandDetail(row.id)
      } catch (e) {
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    openStatusDialog(row) {
      this.statusForm = { id: row.id, status: row.status }
      this.statusVisible = true
    },
    async changeStatus(id, status, message) {
      await updateDemandStatus(id, status)
      this.$message.success(message)
      this.loadList()
      this.loadStats()
    },
    approveDemand(row) {
      this.$confirm(`确定通过需求「${row.title}」？`, '审核通过', { type: 'success' })
        .then(() => this.changeStatus(row.id, 1, '已通过审核'))
        .catch(() => {})
    },
    rejectDemand(row) {
      this.$confirm(`确定驳回需求「${row.title}」？`, '审核驳回', { type: 'warning' })
        .then(() => this.changeStatus(row.id, 4, '已驳回'))
        .catch(() => {})
    },
    async submitStatus() {
      this.submitting = true
      try {
        await updateDemandStatus(this.statusForm.id, this.statusForm.status)
        this.$message.success('状态已更新')
        this.statusVisible = false
        this.loadList()
        this.loadStats()
      } catch (e) {
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.demands-page {
  padding: 8px;
}
.stats-row {
  margin-bottom: 16px;
}
.stat-card {
  margin-bottom: 16px;
  border: none;
}
.stat-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.stat-label {
  color: #909399;
  font-size: 14px;
  margin-bottom: 8px;
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}
.stat-extra {
  margin-top: 8px;
  font-size: 13px;
  color: #909399;
}
.stat-extra.up {
  color: #10b981;
}
.stat-extra.warn {
  color: #f59e0b;
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #1e3a5f;
}
.table-card {
  border: none;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.toolbar-search {
  width: 320px;
  max-width: 100%;
}
.toolbar-filter {
  width: 140px;
}
.skill-tag {
  margin-right: 6px;
  margin-bottom: 4px;
}
.text-muted {
  color: #c0c4cc;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
</style>

<style>
.demand-detail-dialog .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f2f5;
}
.demand-detail-dialog .el-dialog__body {
  padding: 20px 24px 8px;
}
.demand-detail-dialog .el-dialog__footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #f0f2f5;
}
.demand-detail-dialog .detail-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}
.demand-detail-dialog .detail-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.demand-detail-dialog .demand-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  line-height: 1.4;
}
.demand-detail-dialog .info-list {
  border-top: 1px solid #f0f2f5;
  padding-top: 4px;
}
.demand-detail-dialog .info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f8fafc;
}
.demand-detail-dialog .info-label {
  flex-shrink: 0;
  width: 72px;
  font-size: 14px;
  color: #64748b;
}
.demand-detail-dialog .info-value {
  flex: 1;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  word-break: break-all;
}
.demand-detail-dialog .budget-value {
  color: #10b981;
}
.demand-detail-dialog .info-row-tags .info-value {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}
.demand-detail-dialog .skill-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}
.demand-detail-dialog .desc-section {
  margin-top: 20px;
}
.demand-detail-dialog .desc-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}
.demand-detail-dialog .desc-content {
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 14px;
  color: #475569;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}
.demand-detail-dialog .detail-footer {
  text-align: right;
}
</style>
